/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type {
  ChatService,
  Goal,
  IMediaFile,
  LanguageModel,
  SearchResult,
  StoredMediaFile,
  Transcript,
  TranscriptModel,
  TranscriptSummary,
  UserAuth
} from '@repo/common';
import type { AssistantEvents } from '@repo/common/types/AssistantMachine';
import type { AdapterConfig } from '@repo/common/types/Adapter';
import type { SyncDirection, SyncItemData, SyncItemsInput } from '@repo/common/types/AdapterSync';
import type { ConversationModel } from '@repo/common/types/Conversation';
import type { IStorageController } from '@repo/common/types/IStorageController';
import type { MessageModel } from '@repo/common/types/Message';
import type { NotificationController } from '@repo/common/types/NotificationController';
import type { RecordingController } from '@repo/common/types/RecordController';
import type {
  FileUploadOptions,
  FileUploadResult,
  SharedLibHandler,
  TableName
} from '@repo/common/types/SharedLib';
import { get } from 'svelte/store';

import type { Asset, AssetModel, AssetSummary, AssetType } from '@repo/common/types/Asset';
import type { StoredCalendarEventModel } from '@repo/common/types/StoredCalendarEvent';
import type { TaskModel } from '@repo/common/types/Task';
import type { WorkspaceModel } from '@repo/common/types/Workspace';
import {
  createClientAdapter,
  getTableNamesInSyncOrder
} from '@repo/sharedlib/src/adapter/clientAdapterTables';
import { commonToPostgres } from '@repo/sharedlib/src/adapter/clientSync';
import { DataPipe } from '@repo/sharedlib/src/lib/DataPipe';
import type { MediaFile } from '@repo/sharedlib/src/models/mediaFile';
import { dataLoadState } from '@repo/sharedlib/src/stores/cloud.sync.store';
import { refreshTranscriptsStore, transcripts } from '@repo/sharedlib/src/stores/transcripts';
import { BrowserNotificationController } from './BrowserNotificationController';
import { BrowserStorageController } from './storage/BrowserStorageController';
/**
 * RecordingController implementation for the browser environment
 * Uses Web Audio API with FLAC encoding for streaming transcription
 */
export class BrowserRecordingController implements RecordingController {
  private webRecordingStore: typeof import('./lib/webRecordingState').webRecordingStore | null = null;
  private webFlacRecordingStore: typeof import('./lib/webFlacRecordingState').webFlacRecordingStore | null = null;
  private useFlacStreaming = true; // Use FLAC streaming for live transcription

  // Static callback for sending events to the assistant state machine
  // This is set by the web app's assistant.ts to enable proper state machine integration
  private static assistantEventSender: ((event: AssistantEvents) => void) | null = null;

  /**
   * Register a callback to send events to the assistant state machine.
   * Called by apps/web assistant.ts during initialization.
   */
  static setAssistantEventSender(sender: (event: AssistantEvents) => void): void {
    BrowserRecordingController.assistantEventSender = sender;
  }

  /**
   * Send an event to the assistant state machine if a sender is registered.
   */
  private static sendAssistantEvent(event: AssistantEvents): void {
    if (BrowserRecordingController.assistantEventSender) {
      BrowserRecordingController.assistantEventSender(event);
    } else {
      console.warn('[BrowserRecordingController] No assistant event sender registered, event not sent:', event.type);
    }
  }

  private async getRecordingStore() {
    if (!this.webRecordingStore) {
      const { webRecordingStore } = await import('./lib/webRecordingState');
      this.webRecordingStore = webRecordingStore;
    }
    return this.webRecordingStore;
  }

  private async getFlacRecordingStore() {
    if (!this.webFlacRecordingStore) {
      const { webFlacRecordingStore } = await import('./lib/webFlacRecordingState');
      this.webFlacRecordingStore = webFlacRecordingStore;
    }
    return this.webFlacRecordingStore;
  }

  signalTranscriptionComplete(transcriptId: string, streaming: boolean = false): void {
    console.log('[BrowserRecordingController] Transcription complete:', transcriptId, 'streaming:', streaming);
    if (streaming) {
      BrowserRecordingController.sendAssistantEvent({
        type: 'TRANSCRIPTION_COMPLETE',
        transcriptId,
        streaming
      });
    } else {
      BrowserRecordingController.sendAssistantEvent({
        type: 'FINAL_TRANSCRIPTION_COMPLETE',
        transcriptId
      });
    }
  }

  async startNewRecording(title?: string): Promise<void> {
    console.log('[BrowserRecordingController] Starting new recording:', title);

    // Import necessary modules
    const { transcripts } = await import('@repo/sharedlib/src/stores/transcripts');
    const { newCalendarEvent } = await import('@repo/toolslib/src/utils/calendar');

    // Create calendar event for the recording
    const calendarEvent = newCalendarEvent({ title: title || 'New Recording' });

    // Create a transcript entry
    const transcript = await transcripts.createFromData(
      undefined, // No media file for web recording
      calendarEvent,
      0, // Duration starts at 0
      'transcribing', // Status - use 'transcribing' to indicate active recording
      undefined, // workspaceId
      'web-recording' // captureType
    );

    await this._internalStartRecording(transcript.id);
    await this._navigateToTranscript(transcript.id);
  }

  async startExistingRecording(transcriptId: string): Promise<void> {
    console.log('[BrowserRecordingController] Starting existing recording:', transcriptId);
    await this._internalStartRecording(transcriptId);
  }

  async startMeetingRecording(calendarEvent: any): Promise<void> {
    console.log('[BrowserRecordingController] Starting meeting recording:', calendarEvent?.title);

    const { transcripts } = await import('@repo/sharedlib/src/stores/transcripts');

    // Create a transcript entry for the meeting
    const transcript = await transcripts.createFromData(
      undefined,
      calendarEvent,
      0,
      'transcribing', // Use 'transcribing' to indicate active recording
      undefined,
      'web-meeting-recording'
    );

    await this._internalStartRecording(transcript.id);
    await this._navigateToTranscript(transcript.id);
  }

  async abortRecordingAction(_reason: string): Promise<void> {
    console.log('[BrowserRecordingController] Aborting recording:', _reason);

    // Clean up FLAC subscription if exists
    const unsubscribe = (this as any)._flacUnsubscribe;
    if (unsubscribe) {
      unsubscribe();
      (this as any)._flacUnsubscribe = null;
    }

    // Abort FLAC recording if active
    if (this.useFlacStreaming) {
      const flacStore = await this.getFlacRecordingStore();
      const flacState = get(flacStore);
      if (flacState.transcriptId) {
        const { transcripts } = await import('@repo/sharedlib/src/stores/transcripts');
        transcripts.setStatus(flacState.transcriptId, 'empty');
      }
      flacStore.abort();
    }

    // Also abort MediaRecorder recording if active
    const store = await this.getRecordingStore();
    const currentState = get(store);

    if (currentState.transcriptId) {
      const { transcripts } = await import('@repo/sharedlib/src/stores/transcripts');
      transcripts.setStatus(currentState.transcriptId, 'empty');
    }

    store.abort();
  }

  /**
   * Internal method to start recording with either FLAC streaming or MediaRecorder
   */
  private async _internalStartRecording(transcriptId: string): Promise<void> {
    const { transcripts } = await import('@repo/sharedlib/src/stores/transcripts');
    const { devices } = await import('@repo/sharedlib/src/stores/devices');

    const devicesValue = get(devices);
    const deviceId = devicesValue.selectedDevice?.deviceId;
    const deviceName = devicesValue.selectedDevice?.deviceName;

    if (!deviceId) {
      console.warn('[BrowserRecordingController] No audio device selected');
      // Try to get available devices first
      if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputDevices = devices.filter((device) => device.kind === 'audioinput');
        if (audioInputDevices.length === 0) {
          throw new Error('No audio input devices available. Please connect a microphone.');
        }
      }
    }

    console.log('[BrowserRecordingController] Internal start recording for:', transcriptId, {
      deviceId,
      deviceName,
      useFlacStreaming: this.useFlacStreaming
    });

    // Use FLAC streaming for live transcription
    if (this.useFlacStreaming) {
      const flacStore = await this.getFlacRecordingStore();
      await flacStore.start(transcriptId, deviceId || undefined, deviceName || undefined, {
        transcriptionEndpoint: '/api/private/transcribe'
      });

      // Set up subscription to update transcript with streaming transcription
      const unsubscribe = flacStore.subscribe(async (state) => {
        if (state.fullTranscript && state.transcriptId === transcriptId) {
          // Update transcript with latest transcription (rawOutput is string[])
          // Split transcript into lines for the expected format
          const transcriptLines = state.fullTranscript.split('\n').filter((line) => line.trim());
          try {
            await transcripts.updateTranscript(
              state.transcriptId,
              { rawOutput: transcriptLines.length > 0 ? transcriptLines : [state.fullTranscript] },
              false, // Don't force immediate save on every update
              false // Don't sync on every update
            );
          } catch (error) {
            console.error('[BrowserRecordingController] Error updating transcript:', error);
          }
        }
      });

      // Store unsubscribe for cleanup
      (this as any)._flacUnsubscribe = unsubscribe;
    } else {
      // Fallback to MediaRecorder-based recording (no live transcription)
      const store = await this.getRecordingStore();
      await store.start(transcriptId, deviceId || undefined, deviceName || undefined);
    }

    // Update transcript status
    transcripts.setStatus(transcriptId, 'transcribing');
  }

  private async _navigateToTranscript(
    transcriptId: string,
    tabId: string = 'transcription'
  ): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    const path = `/app/docs/${transcriptId}/${tabId}`;
    try {
      const { goto } = await import('$app/navigation');
      await goto(path);
    } catch (error) {
      console.warn('[BrowserRecordingController] Navigation failed, using location:', error);
      window.location.href = path;
    }
  }

  async startTranscribing(transcriptId: string): Promise<void> {
    console.log('[BrowserRecordingController] startTranscribing:', transcriptId);

    const rawBlob = await this.getStoredAudioBlob(transcriptId);
    if (!rawBlob) {
      console.warn(
        '[BrowserRecordingController] No stored audio found for transcription:',
        transcriptId
      );
      return;
    }

    const { transcripts } = await import('@repo/sharedlib/src/stores/transcripts');
    transcripts.setStatus(transcriptId, 'transcribing');

    try {
      const { convertBlobToFlac } = await import('./lib/flacEncoder');
      const flacBlob = await convertBlobToFlac(rawBlob, { sampleRate: 16000 });

      // For now, let's just send the whole thing. If it's over 60s, it might fail on the backend
      // due to Google Speech-to-Text synchronous recognize limit (60s).
      // TODO: Implement client-side chunking for existing blobs

      const response = await fetch('/api/private/transcribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'audio/flac',
          'X-Chunk-Id': 'full',
          'X-Session-Id': transcriptId
        },
        body: flacBlob
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Transcription API failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      if (result.transcript) {
        const transcriptLines = result.transcript.split('\n').filter((line: string) => line.trim());
        await transcripts.updateTranscript(
          transcriptId,
          {
            rawOutput: transcriptLines.length > 0 ? transcriptLines : [result.transcript],
            status: 'transcribed'
          },
          true,
          true
        );
      }
    } catch (error) {
      console.error('[BrowserRecordingController] Failed to transcribe stored blob:', error);
      transcripts.setStatus(transcriptId, 'error');
    }
  }

  async startSummarizing(transcriptId: string): Promise<void> {
    console.log('[BrowserRecordingController] startSummarizing:', transcriptId);

    const { transcripts, getFullTranscript } = await import('@repo/sharedlib/src/stores/transcripts');
    const transcript = await getFullTranscript(transcriptId);

    if (!transcript || !transcript.rawOutput || transcript.rawOutput.length === 0) {
      console.warn('[BrowserRecordingController] No transcript output to summarize:', transcriptId);
      return;
    }

    // Use 'summarizing' state in the store
    const flacStore = await this.getFlacRecordingStore();
    flacStore.setState('summarizing');

    // Use 'transcribing' as a busy state since 'summarizing' is not in TranscriptStatus
    transcripts.setStatus(transcriptId, 'transcribing');

    try {
      const response = await fetch('/api/private/summarize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transcriptId,
          text: transcript.rawOutput.join('\n')
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Summarization API failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      if (result.minutes) {
        await transcripts.addMinutes(transcriptId, result.minutes);
        transcripts.setStatus(transcriptId, 'transcribed'); // Reset status back to transcribed (complete)
      }
    } catch (error) {
      console.error('[BrowserRecordingController] Failed to summarize transcript:', error);
      transcripts.setStatus(transcriptId, 'error');
    } finally {
      const flacStore = await this.getFlacRecordingStore();
      flacStore.setState('idle');
    }
  }

  stopSummarizing(_reason: string): void {
    console.log('[BrowserRecordingController] Stop summarizing:', _reason);
  }

  async stopRecordingAction(_reason: string): Promise<void> {
    console.log('[BrowserRecordingController] Stopping recording');

    // Check if we're using FLAC streaming
    if (this.useFlacStreaming) {
      await this.stopFlacRecording();
      return;
    }

    // Fallback: MediaRecorder-based recording
    const store = await this.getRecordingStore();
    const currentState = get(store);

    if (!currentState.transcriptId) {
      console.warn('[BrowserRecordingController] No active recording to stop');
      return;
    }

    const transcriptId = currentState.transcriptId;
    const audioBlob = await store.stop();

    if (audioBlob) {
      console.log('[BrowserRecordingController] Recording stopped, blob size:', audioBlob.size);

      // Update transcript with recording info
      const { transcripts, getFullTranscript } = await import('@repo/sharedlib/src/stores/transcripts');
      const duration = currentState.duration;

      // Store the audio blob URL for playback
      const audioBlobUrl = URL.createObjectURL(audioBlob);
      console.log('[BrowserRecordingController] Audio blob URL:', audioBlobUrl);

      // Update transcript status and duration
      const transcript = await getFullTranscript(transcriptId);
      if (transcript) {
        await transcripts.updateTranscript(
          transcriptId,
          {
            status: 'transcribed', // Mark as transcribed (recording complete)
            duration: duration,
            fileExists: true,
            data: {
              ...transcript.data,
              captureType: transcript.data?.captureType || 'web-recording'
            }
          },
          true,
          true
        );
      }

      // Store audio metadata in IndexedDB alongside the blob
      console.log('[BrowserRecordingController] Audio stored - URL:', audioBlobUrl, 'Type:', audioBlob.type, 'Size:', audioBlob.size);

      // Store the blob in IndexedDB for persistence
      await this.storeAudioBlob(transcriptId, audioBlob);
    }

    store.reset();
  }

  /**
   * Stop FLAC streaming recording
   */
  private async stopFlacRecording(): Promise<void> {
    const flacStore = await this.getFlacRecordingStore();
    const currentState = get(flacStore);

    if (!currentState.transcriptId) {
      console.warn('[BrowserRecordingController] No active FLAC recording to stop');
      return;
    }

    const transcriptId = currentState.transcriptId;

    // Clean up subscription
    const unsubscribe = (this as any)._flacUnsubscribe;
    if (unsubscribe) {
      unsubscribe();
      (this as any)._flacUnsubscribe = null;
    }

    // Stop recording and wait for all transcriptions to complete
    const result = await flacStore.stop();

    console.log(
      `[BrowserRecordingController] FLAC recording stopped. ` +
      `Duration: ${currentState.duration.toFixed(2)}s, ` +
      `Chunks: ${result?.chunks.length || 0}, ` +
      `Transcript length: ${result?.transcript.length || 0}`
    );

    // Update transcript with final data
    const { transcripts, getFullTranscript } = await import('@repo/sharedlib/src/stores/transcripts');
    const transcript = await getFullTranscript(transcriptId);

    if (transcript && result) {
      // Split final transcript into lines for rawOutput
      const transcriptLines = result.transcript.split('\n').filter(line => line.trim());

      await transcripts.updateTranscript(
        transcriptId,
        {
          status: 'transcribed',
          duration: currentState.duration,
          rawOutput: transcriptLines.length > 0 ? transcriptLines : [result.transcript],
          data: {
            ...transcript.data,
            captureType: transcript.data?.captureType || 'web-recording'
          }
        },
        true,
        true
      );

      // Store the full audio blob in IndexedDB if available
      if (result.audioBlob) {
        await this.storeAudioBlob(transcriptId, result.audioBlob);
      }
    }

    flacStore.reset();
  }

  private static readonly AUDIO_DB_NAME = 'secondbrain-audio';
  private static readonly AUDIO_STORE_NAME = 'recordings';

  /**
   * Open the audio IndexedDB database, ensuring the object store exists
   */
  private openAudioDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(
        BrowserRecordingController.AUDIO_DB_NAME,
        1
      );

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(BrowserRecordingController.AUDIO_STORE_NAME)) {
          db.createObjectStore(BrowserRecordingController.AUDIO_STORE_NAME, { keyPath: 'transcriptId' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Store the audio blob in IndexedDB for persistence
   */
  private async storeAudioBlob(transcriptId: string, blob: Blob): Promise<void> {
    try {
      const db = await this.openAudioDatabase();
      const tx = db.transaction(BrowserRecordingController.AUDIO_STORE_NAME, 'readwrite');
      const store = tx.objectStore(BrowserRecordingController.AUDIO_STORE_NAME);

      store.put({
        transcriptId,
        blob,
        createdAt: new Date().toISOString(),
        mimeType: blob.type,
        size: blob.size
      });

      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      });

      console.log('[BrowserRecordingController] Audio blob stored in IndexedDB');
    } catch (error) {
      console.error('[BrowserRecordingController] Failed to store audio blob:', error);
    }
  }

  /**
   * Retrieve an audio blob from IndexedDB
   */
  async getStoredAudioBlob(transcriptId: string): Promise<Blob | null> {
    try {
      const db = await this.openAudioDatabase();
      const tx = db.transaction(BrowserRecordingController.AUDIO_STORE_NAME, 'readonly');
      const store = tx.objectStore(BrowserRecordingController.AUDIO_STORE_NAME);
      const getRequest = store.get(transcriptId);

      return await new Promise((resolve, reject) => {
        getRequest.onsuccess = () => {
          db.close();
          resolve(getRequest.result?.blob || null);
        };
        getRequest.onerror = () => {
          db.close();
          reject(getRequest.error);
        };
      });
    } catch (error) {
      console.error('[BrowserRecordingController] Failed to retrieve audio blob:', error);
      return null;
    }
  }

  async deleteRecording(
    transcriptId: string,
    _confirmWithDialog: boolean = true
  ): Promise<boolean> {
    console.log('[BrowserRecordingController] Deleting recording:', transcriptId);

    try {
      const { transcripts } = await import('@repo/sharedlib/src/stores/transcripts');
      await transcripts.deleteFile(transcriptId);

      // Also delete from IndexedDB
      await this.deleteStoredAudioBlob(transcriptId);

      return true;
    } catch (error) {
      console.error('[BrowserRecordingController] Failed to delete recording:', error);
      return false;
    }
  }

  async deleteRecordings(transcriptIds: string[]): Promise<boolean> {
    console.log('[BrowserRecordingController] Deleting recordings:', transcriptIds);

    let success = true;
    for (const id of transcriptIds) {
      const result = await this.deleteRecording(id, false);
      if (!result) success = false;
    }
    return success;
  }

  private async deleteStoredAudioBlob(transcriptId: string): Promise<void> {
    try {
      const db = await this.openAudioDatabase();
      const tx = db.transaction(BrowserRecordingController.AUDIO_STORE_NAME, 'readwrite');
      const store = tx.objectStore(BrowserRecordingController.AUDIO_STORE_NAME);

      store.delete(transcriptId);

      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      });
    } catch (error) {
      // Ignore NotFoundError - if the store doesn't exist, there's nothing to delete
      if (error instanceof DOMException && error.name === 'NotFoundError') {
        return;
      }
      console.error('[BrowserRecordingController] Failed to delete audio blob:', error);
    }
  }

  async ensureAllDirectories(): Promise<void> {
    // No directories needed for browser storage
  }

  stopTranscribing(_transcriptId: string): void {
    console.log('[BrowserRecordingController] Stop transcribing:', _transcriptId);
  }
}

export class BrowserLib implements SharedLibHandler {
  dataPipe: DataPipe;
  private isInitialized = false;
  private savedUserId: string | null = null;
  private _databaseModels: any = null;
  private _databaseModelsPromise: Promise<any> | null = null;
  private _databasePrewarmStarted = false;
  private _databasePrewarmAttempts = 0;
  private _sqlitePrefetchStarted = false;
  private _unloadListenerBound = false;
  private _storagePersistChecked = false;
  private _orm: any = null;
  private _ormPromise: Promise<any> | null = null;
  storageController: IStorageController;
  private _notificationController: NotificationController | null = null;
  private _chatService: ChatService | null = null;
  private _cloudSyncPromise: Promise<void> | null = null;

  constructor() {
    this.savedUserId = null;
    this.dataPipe = new DataPipe();
    this.storageController = new BrowserStorageController();
    // Auto-initialize notification controller for browser
    this._notificationController = new BrowserNotificationController();
  }
  saveAssetsLocal?(_assets: Asset[]): Promise<void> {
    throw new Error('Method not implemented.');
  }
  exportToGoogleDocs(
    _title: string,
    _htmlContent: string,
    _existingDocId?: string | null
  ): Promise<{
    success: boolean;
    docId?: string;
    docUrl?: string;
    statusCode?: number;
    error?: string;
  }> {
    throw new Error('Method not implemented.');
  }
  openDrivePickerWindow(_accessToken: string): Promise<void> {
    throw new Error('Method not implemented.');
  }

  convertMediaFile(
    _file: IMediaFile,
    _outputFormat: 'wav' | 'mp3',
    _useTemp: boolean,
    _createMp3IfMissing: boolean
  ): Promise<void> {
    throw new Error('Method not implemented.');
  }

  async convertMediaFileOptimized(_file: IMediaFile): Promise<void> {
    console.debug('convertMediaFileOptimized not implemented in browserlib');
  }

  signalTranscriptionComplete(_transcriptId: string, _streaming: boolean = false): void {
    console.debug('signalTranscriptionComplete not implemented in browserlib');
  }

  async initialize(): Promise<void> {
    console.log('BrowserLib initializing...');
    if (typeof window === 'undefined' || !window.localStorage) {
      throw new Error('Browser environment or localStorage not available.');
    }
    // Example: Check for storage quota or perform setup
    this.isInitialized = true;
    this._bindUnloadListeners();
    void this._ensureStoragePersistence();
    this._prefetchSqliteAssets();
    this._prewarmDatabaseModels();
    console.log('BrowserLib initialized.');
  }

  async attachConsole(): Promise<() => void> {
    return () => { };
  }

  private ensureInitialized(): void {
    if (!this.isInitialized) {
      throw new Error('BrowserLib has not been initialized. Call initialize() first.');
    }
  }

  async createWindowReloadListener(): Promise<() => void> {
    return () => { };
  }

  async getRecordingStatus(): Promise<
    'idle' | 'initializing' | 'recording' | 'saving' | 'deleting'
  > {
    return 'idle';
  }

  setChatService(service: ChatService): void {
    this._chatService = service;
  }

  get notificationController(): NotificationController {
    if (!this._notificationController) {
      throw new Error('NotificationController not initialized.');
    }
    return this._notificationController;
  }

  get chatService(): ChatService {
    if (!this._chatService) {
      throw new Error('ChatService not initialized. Call setChatService() first.');
    }
    return this._chatService;
  }

  async getCurrentWebview<T>(): Promise<T> {
    return null as unknown as T;
  }

  getUserId(): string | null {
    this.ensureInitialized();
    if (!this.savedUserId) {
      console.debug('User ID not set', { detail: 'getUserId' });
      return null;
    }
    return this.savedUserId;
  }

  setUserId(userId: string | null): void {
    this.savedUserId = userId;
  }

  hasUserId(): boolean {
    return !!this.savedUserId;
  }

  openSignInPage(): void {
    window.location.href = '/sign-in';
  }

  async signOutSession(): Promise<void> {
    const { signOut } = await import('@auth/sveltekit/client');

    // Invalidate DataPipe cache to ensure fresh data on next sign in
    this.dataPipe.invalidateCache();

    // Clear transcripts to prevent data leakage between users
    const { transcripts } = await import('@repo/sharedlib/src/stores/transcripts');
    transcripts.update((t) => {
      t.idMap.clear();
      t.eventMap.clear();
      return t;
    });

    console.log('Cleared transcripts on sign out');
    return signOut();
  }
  async importMedia(_path: string): Promise<string> {
    throw Error('Not implemented.');
  }

  async getWindowLabel(): Promise<string> {
    return 'browser';
  }

  getPostOnboardingRedirect(): string {
    return '/welcome';
  }

  async getAvailableDevicesMap(_cacheDurationMs?: number): Promise<Map<string, string>> {
    // Use Web Audio API to enumerate audio input devices
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
      console.warn('[BrowserLib] MediaDevices API not available');
      return new Map();
    }

    try {
      // Enumerate devices without requesting permission
      // Device labels will be empty if permission hasn't been granted yet
      // Permission is requested only when the user actually starts recording
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputDevices = devices.filter(device => device.kind === 'audioinput');

      const deviceMap = new Map<string, string>();
      audioInputDevices.forEach((device, index) => {
        // Use device.deviceId as key and device.label as value
        // If label is empty (permission not yet granted), use a generic name
        const deviceId = device.deviceId || `device-${index}`;
        const deviceLabel = device.label || `Microphone ${index + 1}`;
        deviceMap.set(deviceId, deviceLabel);
      });

      console.log('[BrowserLib] Found audio input devices:', deviceMap.size);
      return deviceMap;
    } catch (error) {
      console.error('[BrowserLib] Error enumerating audio devices:', error);
      return new Map();
    }
  }

  async create16bitWav(_file: MediaFile): Promise<void> {
    // No-op in browser
  }

  async getCreatedTimestamp(_file: IMediaFile): Promise<null | Date> {
    return null;
  }

  async getModifiedTimestamp(_file: IMediaFile): Promise<null | Date> {
    return null;
  }

  async getDuration(_file: MediaFile): Promise<number> {
    return 0;
  }

  async getWaveDir(): Promise<string> {
    return '';
  }

  async saveGoals(_goals1: Goal[]): Promise<any> {
    return null;
  }

  async saveTranscriptsLocal(_transcripts: Transcript[]): Promise<void> {
    // No-op in browser
  }

  getFileMetaData(_path: string): any {
    return null;
  }

  getFileLastModifiedDate(_path: string): any {
    return null;
  }

  isDatabaseLoaded(): boolean {
    return !!this._databaseModels;
  }

  async fromStoredMediaFile(_stored: StoredMediaFile): Promise<IMediaFile> {
    return _stored as unknown as IMediaFile;
  }

  async getDatabaseModels(): Promise<{
    transcriptModel: TranscriptModel;
    messageModel: MessageModel;
    taskModel: TaskModel;
    workspaceModel: WorkspaceModel;
    conversationModel: ConversationModel;
    calendarEventModel: StoredCalendarEventModel;
    assetModel: AssetModel;
  }> {
    // Return cached models if available
    if (this._databaseModels) {
      return this._databaseModels;
    }

    // If there's already a promise in progress, return it
    if (this._databaseModelsPromise) {
      console.log('⏳ Database models initialization already in progress');
      return this._databaseModelsPromise;
    }

    // Wrap the initialization with a timeout to prevent indefinite hangs
    const INIT_TIMEOUT = 60_000; // 30 seconds
    this._databaseModelsPromise = Promise.race([
      this._initializeDatabaseModels(),
      new Promise<any>((_, reject) =>
        setTimeout(
          () => reject(new Error(`Database initialization timed out after ${INIT_TIMEOUT}ms`)),
          INIT_TIMEOUT
        )
      )
    ]);

    try {
      const result = await this._databaseModelsPromise;
      return result;
    } catch (error) {
      // Clear the promise on error so we can retry
      this._databaseModelsPromise = null;
      throw error;
    }
  }

  private _prewarmDatabaseModels(): void {
    if (this._databasePrewarmStarted) {
      return;
    }
    this._databasePrewarmStarted = true;
    this._databasePrewarmAttempts += 1;
    void this.getDatabaseModels().catch((error) => {
      this._databasePrewarmStarted = false;
      console.warn('[BrowserLib] Database prewarm failed:', error);
      if (this._databasePrewarmAttempts < 3) {
        setTimeout(() => {
          this._prewarmDatabaseModels();
        }, 2000);
      }
    });
  }

  private _prefetchSqliteAssets(): void {
    if (this._sqlitePrefetchStarted || typeof window === 'undefined' || typeof fetch !== 'function') {
      return;
    }
    this._sqlitePrefetchStarted = true;
    const urls = [
      '/wasm/sqlite3-worker1-bundler-friendly.mjs',
      '/wasm/sqlite3-bundler-friendly.mjs',
      '/wasm/sqlite3.wasm',
      '/wasm/sqlite3-worker1-promiser.js',
      '/wasm/sqlite3-opfs-async-proxy.js'
    ];
    void Promise.allSettled(
      urls.map((url) =>
        fetch(url, {
          cache: 'force-cache',
          credentials: 'omit'
        })
      )
    );
  }

  private async _ensureStoragePersistence(): Promise<void> {
    if (this._storagePersistChecked || typeof navigator === 'undefined') {
      return;
    }
    this._storagePersistChecked = true;
    try {
      const storage = (navigator as any).storage;
      if (!storage || typeof storage.persisted !== 'function') {
        return;
      }
      const alreadyPersisted = await storage.persisted();
      if (!alreadyPersisted && typeof storage.persist === 'function') {
        const granted = await storage.persist();
        console.log('[BrowserLib] Storage persistence requested:', granted);
      }
    } catch (error) {
      console.warn('[BrowserLib] Failed to request storage persistence:', error);
    }
  }

  private _bindUnloadListeners(): void {
    if (this._unloadListenerBound || typeof window === 'undefined') {
      return;
    }
    const handleUnload = () => {
      // On page unload, force terminate the worker immediately
      // beforeunload/pagehide don't wait for async operations
      this._closeDatabaseOnUnload(true);
    };
    window.addEventListener('pagehide', handleUnload);
    window.addEventListener('beforeunload', handleUnload);
    this._unloadListenerBound = true;
  }

  private _closeDatabaseOnUnload(forceTerminate: boolean = false): void {
    if (!this._orm) {
      return;
    }
    try {
      // ORM.close(true) now handles synchronous worker termination
      if (typeof this._orm.close === 'function') {
        this._orm.close(forceTerminate);
      }
    } catch (error) {
      console.warn('[BrowserLib] Failed to close database on unload:', error);
    }
  }

  private async _initializeORM(): Promise<any> {
    const now = () =>
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();
    const logTiming = (label: string, startMs: number) => {
      const durationMs = Math.round((now() - startMs) * 100) / 100;
      console.log(`[BrowserLib] ${label} in ${durationMs}ms`);
    };
    // Import sqlite-wasm ORM only when needed
    const importStart = now();
    const { createSqliteWasmORM } = await import('@repo/tauri-sql-orm/sqlite-wasm');
    logTiming('SQLite WASM ORM import', importStart);

    // Create SQLite WASM ORM instance backed by OPFS (persistent in-browser filesystem)
    // Any non-':memory:' path uses OPFS via vfs=opfs in the driver
    const createStart = now();
    const orm = await createSqliteWasmORM('secondbrain.db');
    logTiming('SQLite WASM ORM create', createStart);
    return orm;
  }

  /**
   * Smart cloud sync: Triggers sync if needed based on:
   * - First run (no data in database)
   * - Haven't synced recently (>5 minutes)
   * - Database is empty (total items across all tables is 0)
   */
  private async _smartCloudSync(firstRun: boolean): Promise<void> {
    // If a sync is already in progress, return that promise
    if (this._cloudSyncPromise) {
      console.log('[BrowserLib] Cloud sync already in progress, skipping');
      return this._cloudSyncPromise;
    }

    // Start the sync logic
    this._cloudSyncPromise = (async () => {
      try {
        const { config } = await import('@repo/sharedlib/src/stores/config');

        const $config = get(config);

        // Check if we should sync
        const now = Date.now();
        const lastSyncTime = $config.lastCloudSyncAt
          ? new Date($config.lastCloudSyncAt).getTime()
          : 0;
        const timeSinceLastSync = now - lastSyncTime;
        const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes

        const shouldSyncByTime = !$config.lastCloudSyncAt || timeSinceLastSync > SYNC_INTERVAL;

        // Check if database is empty
        let isDatabaseEmpty = false;
        if (!firstRun && !shouldSyncByTime) {
          try {
            const driver = await (this._orm as any).getDriver();

            // Count total items across major tables
            const [transcriptCount] = await driver.select(
              'SELECT COUNT(*) as count FROM transcript WHERE deletedAt IS NULL'
            );
            const [taskCount] = await driver.select(
              'SELECT COUNT(*) as count FROM task WHERE deletedAt IS NULL'
            );
            const [assetCount] = await driver.select(
              'SELECT COUNT(*) as count FROM asset WHERE deletedAt IS NULL'
            );

            const totalItems =
              (transcriptCount?.count || 0) + (taskCount?.count || 0) + (assetCount?.count || 0);

            isDatabaseEmpty = totalItems === 0;
            console.log('[BrowserLib] Database item counts:', {
              transcripts: transcriptCount?.count || 0,
              tasks: taskCount?.count || 0,
              assets: assetCount?.count || 0,
              total: totalItems
            });
          } catch (error) {
            console.warn('[BrowserLib] Failed to check database counts:', error);
          }
        }

        const shouldSync = firstRun || shouldSyncByTime || isDatabaseEmpty;

        if (shouldSync) {
          const reason = firstRun
            ? 'first run'
            : isDatabaseEmpty
              ? 'database is empty'
              : 'sync interval exceeded';
          console.log(`[BrowserLib] Cloud sync recommended (${reason}), but skip for now to avoid circular dependency`);
          // TODO: Trigger sync via a callback or event instead of direct import
          // await startSync();
        } else {
          console.log(
            `[BrowserLib] Skipping cloud sync (last sync: ${Math.round(timeSinceLastSync / 1000)}s ago)`
          );
          dataLoadState.set('complete');
        }
      } catch (error) {
        console.error('[BrowserLib] Error during smart cloud sync:', error);
        throw error;
      } finally {
        this._cloudSyncPromise = null;
      }
    })();

    return this._cloudSyncPromise;
  }

  private async _initializeDatabaseModels(): Promise<{
    transcriptModel: TranscriptModel;
    messageModel: MessageModel;
    taskModel: TaskModel;
    workspaceModel: WorkspaceModel;
    conversationModel: ConversationModel;
    calendarEventModel: StoredCalendarEventModel;
    assetModel: AssetModel;
  }> {
    let firstRun = false;
    const now = () =>
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();
    const logTiming = (label: string, startMs: number) => {
      const durationMs = Math.round((now() - startMs) * 100) / 100;
      console.log(`[BrowserLib] ${label} in ${durationMs}ms`);
    };
    const totalStart = now();
    try {
      console.log('🔄 Initializing SQLite WASM database models...');

      // Check if ORM already exists to prevent duplicate initialization
      if (!this._orm) {
        // If there's already an ORM initialization in progress, wait for it
        if (this._ormPromise) {
          const ormWaitStart = now();
          console.log('⏳ ORM initialization already in progress, waiting...');
          this._orm = await this._ormPromise;
          logTiming('ORM initialization (wait)', ormWaitStart);
        } else {
          // Start ORM initialization
          const ormInitStart = now();
          this._ormPromise = this._initializeORM();
          try {
            this._orm = await this._ormPromise;
          } catch (error) {
            // Clear the promise on error so we can retry
            this._ormPromise = null;
            throw error;
          }
          this._ormPromise = null; // Clear the promise after completion
          logTiming('ORM initialization', ormInitStart);
        }
      } else {
        console.log('🔄 Reusing existing ORM instance');
      }

      // Create model definitions using the existing model classes
      const importsStart = now();
      const { TranscriptModelClass } = await import('@repo/sharedlib/src/lib/db/transcript.lite');
      const { MessageModelClass } = await import('@repo/sharedlib/src/lib/db/message.lite');
      const { WorkspaceModelClass } = await import('@repo/sharedlib/src/lib/db/workspace.lite');
      const { ConversationModelClass } = await import(
        '@repo/sharedlib/src/lib/db/conversation.lite'
      );
      const { StoredCalendarEventModelClass } = await import(
        '@repo/sharedlib/src/lib/db/calendarEvent.lite'
      );
      const { AssetModelClass } = await import('./lib/db/asset.lite');
      logTiming('Dynamic model imports', importsStart);

      const defineDbStart = now();
      const transcriptModelDefinition = await TranscriptModelClass.defineDb(this._orm);
      const messageModelDefinition = await MessageModelClass.defineDb(this._orm);
      const workspaceModelDefinition = await WorkspaceModelClass.defineDb(this._orm);
      const conversationModelDefinition = await ConversationModelClass.defineDb(this._orm);
      const calendarEventModelDefinition = await StoredCalendarEventModelClass.defineDb(this._orm);
      const assetModelDefinition = await AssetModelClass.defineDb(this._orm);
      logTiming('Model defineDb', defineDbStart);

      // Determine if this is a first-run or schema update is needed
      let forceSync = false;
      try {
        const syncStateStart = now();
        const driver = await (this._orm as any).getDriver();
        const tableRows = await driver.select(
          "SELECT name FROM sqlite_master WHERE type='table' AND name IN (?,?,?,?,?,?,?)",
          ['transcript', 'message', 'workspace', 'conversation', 'calendarEvent', 'task', 'asset']
        );
        const existingCount = Array.isArray(tableRows) ? tableRows.length : 0;
        firstRun = existingCount === 0;

        // Check if conversation table has the correct schema (has 'name' column)
        let schemaUpdateNeeded = false;
        if (!firstRun) {
          try {
            const conversationSchema = await driver.select('PRAGMA table_info(conversation)');
            const hasNameColumn = conversationSchema.some((col: any) => col.name === 'name');
            if (!hasNameColumn) {
              console.log('Conversation table missing "name" column, forcing schema update');
              schemaUpdateNeeded = true;
            }
          } catch (e) {
            console.warn('Could not check conversation schema, forcing sync:', e);
            schemaUpdateNeeded = true;
          }
        }

        forceSync = firstRun || schemaUpdateNeeded;
        console.log(
          `SQLite WASM sync mode: ${forceSync ? 'force' : 'normal'
          } (existing tables: ${existingCount}, schema update needed: ${schemaUpdateNeeded})`
        );
        logTiming('Sync state detection', syncStateStart);
      } catch (e) {
        console.warn('Could not determine sync state, proceeding with force sync:', e);
        forceSync = true;
      }

      // Sync all models to create tables (use force when needed)
      const syncStart = now();
      await Promise.all([
        transcriptModelDefinition.sync(forceSync ? { force: true } : undefined),
        messageModelDefinition.sync(forceSync ? { force: true } : undefined),
        workspaceModelDefinition.sync(forceSync ? { force: true } : undefined),
        conversationModelDefinition.sync(forceSync ? { force: true } : undefined),
        calendarEventModelDefinition.sync(forceSync ? { force: true } : undefined),
        assetModelDefinition.sync(forceSync ? { force: true } : undefined)
      ]);
      logTiming('Model sync', syncStart);

      // Task model will be created below; ensure its table and indexes after creation

      console.log('✅ SQLite WASM database models initialized');

      // Create model instances using the existing model classes
      const createModelInstance = (ModelClass: any, modelDefinition: any, _modelName: string) => {
        const instance = new ModelClass(modelDefinition);

        // Add missing methods that might be expected by the adapter system
        if (!instance.findAllObjRaw) {
          instance.findAllObjRaw = async (query: string, bindValues?: unknown[]) => {
            // For raw queries, we need to handle them differently with SQLite WASM
            const driver = await (this._orm as any).getDriver();
            return driver.select(query, bindValues || []);
          };
        }

        return instance;
      };

      const transcriptModel = createModelInstance(
        TranscriptModelClass,
        transcriptModelDefinition,
        'transcript'
      ) as any as TranscriptModel;

      // Ensure transcript table has all required columns and indexes
      if (typeof (transcriptModel as any).ensureIndexes === 'function') {
        const transcriptIndexStart = now();
        await (transcriptModel as any).ensureIndexes();
        logTiming('Indexes: transcript', transcriptIndexStart);
      }

      // One-time migration: Populate summary field from meeting.minutes.summary
      try {
        const migrationStart = now();
        const driver = await (this._orm as any).getDriver();
        const needsMigration = await driver.select(
          'SELECT COUNT(*) as count FROM transcript WHERE summary IS NULL AND meeting IS NOT NULL LIMIT 1'
        );
        if (needsMigration[0]?.count > 0) {
          console.log('[BrowserLib] Migrating summary field from meeting JSON...');
          // Update all transcripts that have meeting data but no summary
          await driver.execute(`
            UPDATE transcript
            SET summary = SUBSTR(json_extract(meeting, '$.minutes.summary'), 1, 500)
            WHERE summary IS NULL
              AND meeting IS NOT NULL
              AND json_extract(meeting, '$.minutes.summary') IS NOT NULL
          `);
          console.log('[BrowserLib] Summary migration complete');
        }
        logTiming('Migration: transcript summary', migrationStart);
      } catch (error) {
        console.warn('[BrowserLib] Failed to migrate summary field:', error);
      }

      const messageModel = createModelInstance(
        MessageModelClass,
        messageModelDefinition,
        'message'
      ) as any as MessageModel;

      // Add debugging to the message model's toRow method to catch serialization issues
      const originalToRow = (messageModel as any).toRow;
      (messageModel as any).toRow = (item: any) => {
        try {
          const row = originalToRow.call(messageModel, item);
          // Ensure all JSON fields are properly serialized
          if (row.data && typeof row.data === 'object') {
            row.data = JSON.stringify(row.data);
          }
          if (row.queryOptions && typeof row.queryOptions === 'object') {
            row.queryOptions = JSON.stringify(row.queryOptions);
          }
          if (row.sources && typeof row.sources === 'object') {
            row.sources = JSON.stringify(row.sources);
          }
          return row;
        } catch (error) {
          console.error('Error in message toRow:', error, 'Item:', item);
          throw error;
        }
      };

      // Create proper Task model using SqliteWasmMixin
      let taskModel: any;
      try {
        const { TaskModelClass } = await import('./lib/db/task.lite');
        const taskCreateStart = now();
        console.log('🔍 Creating TaskModelClass with ORM:', !!this._orm);
        taskModel = await TaskModelClass.createWithDb(this._orm);
        // Ensure schema upgrades and indexes are applied
        if (typeof taskModel.ensureIndexes === 'function') {
          await taskModel.ensureIndexes();
        }
        console.log('✅ TaskModelClass created successfully');
        logTiming('Task model create + indexes', taskCreateStart);
      } catch (error) {
        console.error('❌ Error creating TaskModelClass:', error);
        throw error;
      }

      const workspaceModel = createModelInstance(
        WorkspaceModelClass,
        workspaceModelDefinition,
        'workspace'
      ) as any as WorkspaceModel;

      if (typeof (workspaceModel as any).ensureIndexes === 'function') {
        const workspaceIndexStart = now();
        await (workspaceModel as any).ensureIndexes();
        logTiming('Indexes: workspace', workspaceIndexStart);
      }

      const conversationModel = createModelInstance(
        ConversationModelClass,
        conversationModelDefinition,
        'conversation'
      ) as any as ConversationModel;

      if (typeof (conversationModel as any).ensureIndexes === 'function') {
        const conversationIndexStart = now();
        await (conversationModel as any).ensureIndexes();
        logTiming('Indexes: conversation', conversationIndexStart);
      }

      const calendarEventModel = createModelInstance(
        StoredCalendarEventModelClass,
        calendarEventModelDefinition,
        'calendarEvent'
      ) as any as StoredCalendarEventModel;

      if (typeof (calendarEventModel as any).ensureIndexes === 'function') {
        const calendarIndexStart = now();
        await (calendarEventModel as any).ensureIndexes();
        logTiming('Indexes: calendarEvent', calendarIndexStart);
      }

      const assetModel = createModelInstance(
        AssetModelClass,
        assetModelDefinition,
        'asset'
      ) as any as AssetModel;

      if (typeof (assetModel as any).ensureIndexes === 'function') {
        const assetIndexStart = now();
        await (assetModel as any).ensureIndexes();
        logTiming('Indexes: asset', assetIndexStart);
      }

      const models = {
        transcriptModel,
        messageModel,
        taskModel,
        workspaceModel,
        conversationModel,
        calendarEventModel,
        assetModel
      };

      // Cache the models
      this._databaseModels = models;
      this._databaseModelsPromise = null;

      // Smart cloud sync: Trigger sync if needed (don't await - run in background)
      this._smartCloudSync(firstRun).catch((error: unknown) => {
        console.debug('[BrowserLib] Smart cloud sync failed:', error);
      });

      logTiming('Total _initializeDatabaseModels', totalStart);
      return models;
    } catch (error) {
      console.error('❌ Error initializing SQLite WASM database models:', JSON.stringify(error));
      throw new Error(`Failed to initialize database models: ${error}`);
    }
  }

  async getMessageModel(): Promise<MessageModel> {
    const models = await this.getDatabaseModels();
    return models.messageModel;
  }

  async getTranscriptModel(): Promise<TranscriptModel> {
    const models = await this.getDatabaseModels();
    return models.transcriptModel;
  }

  async getTaskModel(): Promise<TaskModel> {
    const models = await this.getDatabaseModels();
    return models.taskModel;
  }

  async getWorkspaceModel(): Promise<any> {
    const models = await this.getDatabaseModels();
    return models.workspaceModel;
  }

  async getConversationModel(): Promise<any> {
    const models = await this.getDatabaseModels();
    return models.conversationModel;
  }

  async getCalendarEventModel(): Promise<any> {
    const models = await this.getDatabaseModels();
    return models.calendarEventModel;
  }

  async getAssetModel(): Promise<AssetModel> {
    const models = await this.getDatabaseModels();
    return models.assetModel;
  }

  async waitForSqliteReady(): Promise<void> {
    // Ensure ORM and driver are initialized and responsive
    if (!this._orm) {
      await this.getDatabaseModels();
    }
    const ormAny = this._orm as any;
    if (ormAny && typeof ormAny.getDriver === 'function') {
      const driver = await ormAny.getDriver();
      if (driver && typeof (driver as any).waitUntilReady === 'function') {
        await (driver as any).waitUntilReady();
      }
    }
  }

  clearDatabaseModelsCache(): void {
    // No-op in browser
  }

  async loadFromDB(force?: boolean, limit = 1000): Promise<void> {
    const { loadFromDB: sharedLoadFromDB } = await import('@repo/sharedlib/src/lib/db/loadFromDB');
    await sharedLoadFromDB(force);
    await transcripts.loadSummaries(force, limit ?? 1000);
    refreshTranscriptsStore();
  }

  async loadGoalsFromCache(): Promise<void> {
    // No-op in browser
  }

  /**
   * Attempt to recover from an unrecoverable OPFS state by:
   * - closing the current driver/ORM
   * - deleting the OPFS-backed DB file
   * - reinitializing models
   * Returns true on success
   */
  async resetLocalDatabase(): Promise<boolean> {
    try {
      // Close current ORM/driver if present
      if (this._orm && typeof (this._orm as any).getDriver === 'function') {
        try {
          const driver = await (this._orm as any).getDriver();
          if (driver && typeof driver.close === 'function') {
            await driver.close();
          }
        } catch (err) {
          console.warn('Error closing existing driver during reset:', err);
        }
      }

      // Clear caches
      this._databaseModels = null;
      this._databaseModelsPromise = null;
      this._orm = null;
      this._ormPromise = null;

      // Delete OPFS DB file and reinitialize
      try {
        const storage: any = (navigator as any)?.storage;
        if (storage && typeof storage.getDirectory === 'function') {
          const root: any = await storage.getDirectory();
          await root.removeEntry('secondbrain.db', { recursive: false });
        }
      } catch (err) {
        console.warn('Could not delete OPFS DB; continuing anyway', err);
      }

      await this.getDatabaseModels();
      console.log('✅ Local database has been reset and reinitialized');
      return true;
    } catch (error) {
      console.error('❌ Failed to reset local database:', error);
      return false;
    }
  }

  openLink(link: string): void {
    window.open(link, '_blank');
  }

  async getRecordingsDir(): Promise<string> {
    return '';
  }

  async createTauriListener(
    _eventType: string,
    _handler: (event: any) => void
  ): Promise<() => void> {
    return () => { };
  }

  async emitEvent(): Promise<void> {
    // No-op in browser
  }

  async modelIsDownloaded(_model: LanguageModel): Promise<boolean> {
    return false;
  }

  async checkPermissions(_openDialog: boolean): Promise<boolean | import('@repo/common/types/SharedLib').PermissionStatus> {
    // Check microphone permission using Permissions API
    if (typeof navigator === 'undefined' || !navigator.permissions) {
      // Permissions API not available, assume granted
      return { screenRecording: true, microphone: true };
    }

    try {
      const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      const microphoneGranted = micPermission.state === 'granted';

      // If permission is 'prompt' and _openDialog is true, try to trigger the prompt
      if (micPermission.state === 'prompt' && _openDialog) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach(track => track.stop());
          return { screenRecording: true, microphone: true };
        } catch {
          return { screenRecording: true, microphone: false };
        }
      }

      return { screenRecording: true, microphone: microphoneGranted };
    } catch {
      // Permissions API query failed, try getUserMedia directly
      if (_openDialog) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach(track => track.stop());
          return { screenRecording: true, microphone: true };
        } catch {
          return { screenRecording: true, microphone: false };
        }
      }
      // Assume granted if we can't check
      return { screenRecording: true, microphone: true };
    }
  }

  async checkForAppUpdates(
    _updateAndRestart: boolean,
    _promptUser: boolean,
    _alertNoUpdates: boolean,
    _onDismissVersion?: (version: string) => void,
    _shouldShowToast?: (version: string) => boolean
  ): Promise<boolean> {
    return false;
  }

  async searchTranscripts(_queryStr: string, _limit: number): Promise<SearchResult[]> {
    return [];
  }

  async backupSqlite(): Promise<void> {
    // No-op in browser
  }

  async sanitizeSqlite(): Promise<void> {
    // No-op in browser
  }

  async purgeTranscripts(): Promise<void> {
    // No-op in browser
  }

  async deleteTranscriptFromIndex(): Promise<void> {
    // No-op in browser
  }

  async indexTranscripts(_transcripts: Transcript[]): Promise<void> {
    // No-op in browser
  }

  async ensureAllDirectories(): Promise<void> {
    // No-op in browser
  }

  recordingController: RecordingController = new BrowserRecordingController();

  async syncTableAdapter(
    direction: SyncDirection,
    tableName: TableName,
    authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void,
    adapterConfig?: Partial<AdapterConfig>
  ): Promise<void> {
    const models = await this.getDatabaseModels();
    const userId = this.getUserId();
    if (!userId) {
      throw new Error('User ID not set');
    }
    const adapter = createClientAdapter(tableName, models, userId, authToken, adapterConfig);
    await adapter.sync(direction, onProgress);
  }

  async syncAdapters(
    direction: SyncDirection,
    authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void,
    adapterConfig?: Partial<AdapterConfig>
  ): Promise<void> {
    const tableNames = getTableNamesInSyncOrder();
    const totalAdapters = tableNames.length;
    let completedAdapters = 0;

    for (const tableName of tableNames) {
      try {
        const adapterProgress = (
          operation: string,
          adapterComplete: number,
          adapterTotal: number
        ) => {
          // Correct aggregate progress: fraction of adapters completed plus fraction of current adapter
          const totalProgress =
            (completedAdapters + (adapterTotal === 0 ? 0 : adapterComplete / adapterTotal)) /
            totalAdapters;

          const totalItems = totalAdapters; // Each adapter counts as 1 "item"
          const itemsComplete = Math.floor(totalProgress * totalItems);

          // Include table name in operation message for better transparency
          const aggregateOperation = operation.replace(
            /Syncing (workspace|task|transcript|message)/,
            `Syncing ${tableName}`
          );

          onProgress?.(aggregateOperation, itemsComplete, totalItems);
        };

        await this.syncTableAdapter(
          direction,
          tableName,
          authToken,
          adapterProgress,
          adapterConfig
        );
      } catch (error) {
        console.warn(`Error syncing ${tableName} adapters:`, error);
      }

      completedAdapters++;
    }
  }

  async syncItems(items: SyncItemsInput, authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>) {
    const userId = this.getUserId();
    if (!userId) {
      throw new Error('User ID not set');
    }
    await commonToPostgres(items, userId, authToken);
  }

  async syncItemData(items: SyncItemData, authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>) {
    const startTimeMs = performance.now();
    const userId = this.getUserId();
    if (!userId) {
      throw new Error('User ID not set');
    }
    const models = await this.getDatabaseModels();
    const tableNames = getTableNamesInSyncOrder();

    for (const tableName of tableNames) {
      const tableData = items[tableName];
      if (!tableData) {
        continue;
      }
      if (tableName === 'calendarEvent') {
        console.log('syncing calendarEvent items:', Object.keys(tableData).length);
      } else {
        console.log('syncing table:', tableName, 'items:', Object.keys(tableData).length);
      }

      try {
        const adapter = createClientAdapter(tableName, models, userId, authToken, { retries: 3 });
        await adapter.upsertCommonListToSqlite(tableData);
        const endTimeMs = performance.now();
        const durationMs = endTimeMs - startTimeMs;
        console.log(`upsertCommonListToSqlite took ${durationMs.toFixed(2)}ms`);
      } catch (error) {
        console.error(`Error processing table ${tableName}:`, error);
        throw error;
      }
    }

    // Note: Removed the syncItems call that was sending minimal items to the server
    // The upsertCommonListToSqlite call above already saves full items to SQLite
    // Bidirectional sync should happen automatically when needed, not as part of syncItemData
    const endTimeMs = performance.now();
    const durationMs = endTimeMs - startTimeMs;
    console.log(`syncItemData took ${durationMs.toFixed(2)}ms`);
  }

  async verifySync(
    authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
    sinceDate?: Date
  ): Promise<void> {
    const userId = this.getUserId();
    if (!userId) {
      console.warn('No user ID available for sync verification');
      return;
    }

    const models = await this.getDatabaseModels();
    const tableNames = getTableNamesInSyncOrder();

    console.log(`Starting sync verification for ${tableNames.length} adapters...`);

    for (const tableName of tableNames) {
      try {
        console.log(`Verifying sync integrity for ${tableName}...`);

        // Create the adapter instance
        const adapter = createClientAdapter(tableName, models, userId, authToken);

        // Verify sync integrity for this adapter
        if (typeof adapter.verifySync === 'function') {
          await adapter.verifySync(sinceDate, tableName === 'message');
        } else {
          console.log(`No verifySync method available for ${tableName}`);
        }
      } catch (error) {
        console.error(`Error verifying sync integrity for ${tableName}:`, error);
        // Continue with other adapters even if one fails
      }
    }

    console.log('Sync verification completed');
  }

  async verifySyncDiagnostic(
    tableName: 'workspace' | 'task' | 'transcript' | 'message' | 'conversation' | 'calendarEvent',
    authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
    sinceDate?: Date
  ): Promise<void> {
    const userId = this.getUserId();
    if (!userId) {
      console.warn('No user ID available for sync verification');
      return;
    }

    const models = await this.getDatabaseModels();

    try {
      console.log(`Verifying sync integrity for ${tableName}...`);

      // Create the adapter instance
      const adapter = createClientAdapter(tableName, models, userId, authToken);

      // Verify sync integrity for this adapter
      if (typeof adapter.verifySync === 'function') {
        await adapter.verifySync(sinceDate, true); // true for diagnostic mode
      } else {
        console.log(`No verifySync method available for ${tableName}`);
      }
    } catch (error) {
      console.error(`Error verifying sync integrity for ${tableName}:`, error);
      throw error;
    }
  }

  async copyHtmlToClipboard(html: string): Promise<void> {
    // Create a temporary div to hold the HTML content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Create a ClipboardItem with both HTML and plain text formats
    const clipboardItem = new ClipboardItem({
      'text/html': new Blob([tempDiv.innerHTML], { type: 'text/html' }),
      'text/plain': new Blob([tempDiv.textContent || ''], { type: 'text/plain' })
    });

    // Write to clipboard
    await navigator.clipboard.write([clipboardItem]);
  }

  async copyTextToClipboard(text: string): Promise<void> {
    // Use browser's clipboard API for web
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Failed to copy text to clipboard:', error);
      throw error;
    }
  }

  async share(options: { title: string; text: string; url: string }): Promise<void> {
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share(options);
      } catch (error) {
        // User cancelled or share failed
        console.debug('Share failed or cancelled:', error);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      console.warn('Web Share API not supported in this browser');
      // Could fallback to copying URL or showing a custom dialog
      await this.copyTextToClipboard(options.url);
    }
  }

  async openSignUpPage(): Promise<void> {
    window.location.href = '/sign-up';
  }

  async removeSqliteDatabase(): Promise<void> {
    // No-op in browser
  }

  async getAppVersion(): Promise<string> {
    return '0.0.0';
  }

  async getOsVersion(): Promise<string> {
    return '0.0.0';
  }

  async openVideoInNewWindow(_videoId: string): Promise<void> {
    // No-op in browser
  }

  async closeCurrentWindow(): Promise<void> {
    // No-op in browser
  }

  async loadTranscriptSummariesFromDB(_force = false, limit = 1000): Promise<TranscriptSummary[]> {
    console.log('[BrowserLib] Loading transcript summaries from database');
    await this.getDatabaseModels(); // Ensure database is initialized
    try {
      // PERFORMANCE OPTIMIZATION: Select only the columns we need for TranscriptSummary
      // This avoids loading heavy JSON fields like rawOutput, editedOutput, output, data
      // which can contain thousands of lines of text and cause 1+ second queries
      const query = `
        SELECT
          id, name, editedName, summary, status, duration, eventId,
          fileExists, isDeleted, isFavorite, workspaceId, date,
          createdAt, updatedAt, deletedAt, data
        FROM transcript
        WHERE deletedAt IS NULL
        ORDER BY createdAt DESC
        LIMIT ?
      `;
      const queryStart = Date.now();

      // Execute raw query using the SQLite WASM driver
      const driver = await (this._orm as any).getDriver();
      const rows = await driver.select(query, [limit]);

      const queryElapsed = Date.now() - queryStart;
      console.log(
        '[BrowserLib] Found transcript summaries from DB',
        rows.length,
        'in',
        `${queryElapsed}ms`
      );

      // Convert rows to TranscriptSummary objects
      const summaries: TranscriptSummary[] = rows.map((row: any) => ({
        ...row,
        _type: 'transcriptSummary' as const,
        // Parse data JSON if present
        data: row.data ? (typeof row.data === 'string' ? JSON.parse(row.data) : row.data) : undefined,
        // Convert SQLite boolean strings/integers to actual booleans
        isDeleted:
          row.isDeleted === 1 ||
          row.isDeleted === '1' ||
          row.isDeleted === 'true' ||
          row.isDeleted === true,
        fileExists:
          row.fileExists === 1 ||
          row.fileExists === '1' ||
          row.fileExists === 'true' ||
          row.fileExists === true,
        isFavorite:
          row.isFavorite === 1 ||
          row.isFavorite === '1' ||
          row.isFavorite === 'true' ||
          row.isFavorite === true,
        date: row.date ? new Date(row.date) : undefined,
        createdAt: row.createdAt ? new Date(row.createdAt) : new Date(),
        updatedAt: row.updatedAt ? new Date(row.updatedAt) : new Date(),
        deletedAt: row.deletedAt ? new Date(row.deletedAt) : undefined
      }));

      console.log('[BrowserLib] Sample transcript (first 3):', summaries.slice(0, 3));
      console.log('[BrowserLib] Returning', summaries.length, 'summaries');

      // Debug: Check for deleted items
      // const deletedCount = summaries.filter((s) => s.isDeleted || s.deletedAt).length;
      // console.log('[BrowserLib] Deleted count:', deletedCount);
      // console.log(
      //   '[BrowserLib] Sample isDeleted values:',
      //   summaries
      //     .slice(0, 10)
      //     .map((s) => ({ id: s.id, isDeleted: s.isDeleted, deletedAt: s.deletedAt }))
      // );

      return summaries;
    } catch (error) {
      console.error('[BrowserLib] Error loading transcript summaries:', error);
      return [];
    }
  }

  async loadAssetSummariesFromDB(_force = false, limit = 1000): Promise<AssetSummary[]> {
    console.log('[BrowserLib] Loading asset summaries from database');
    await this.getDatabaseModels();

    try {
      const driver = await (this._orm as any).getDriver();
      const query = `
        SELECT
          id,
          assetType,
          title,
          summary,
          workspaceId,
          isFavorite,
          createdAt,
          updatedAt,
          deletedAt
        FROM asset
        WHERE deletedAt IS NULL
        ORDER BY updatedAt DESC
        LIMIT ?
      `;

      const start = Date.now();
      const rows = await driver.select(query, [limit]);
      const elapsed = Date.now() - start;

      console.log('[BrowserLib] Found asset summaries', rows.length, 'in', `${elapsed}ms`);

      // Get userId from handler since it's not stored in WASM SQLite
      const currentUserId = this.getUserId() || '';

      return rows.map((row: any) => ({
        _type: 'assetSummary' as const,
        id: row.id,
        assetType: (row.assetType || 'email') as AssetType,
        title: row.title,
        summary: row.summary ?? undefined,
        workspaceId: row.workspaceId ?? undefined,
        isFavorite:
          row.isFavorite === 1 ||
          row.isFavorite === '1' ||
          row.isFavorite === 'true' ||
          row.isFavorite === true,
        userId: currentUserId,
        createdAt: row.createdAt ? new Date(row.createdAt) : new Date(),
        updatedAt: row.updatedAt ? new Date(row.updatedAt) : new Date(),
        deletedAt: undefined
      }));
    } catch (error) {
      console.error('[BrowserLib] Error loading asset summaries:', error);
      return [];
    }
  }

  // Haptic feedback methods - stubs for browser environment
  async triggerTabChangeHaptic(): Promise<void> {
    // No-op for browser environment
  }

  async triggerImpactHaptic(_intensity: 'light' | 'medium' | 'heavy' = 'medium'): Promise<void> {
    // No-op for browser environment
  }

  async triggerNotificationHaptic(
    _type: 'success' | 'warning' | 'error' = 'success'
  ): Promise<void> {
    // No-op for browser environment
  }

  async triggerVibration(_duration: number = 1): Promise<void> {
    // No-op for browser environment
  }

  async setupAppUpdates(
    _assistantState: any,
    _isOnlineStore?: { subscribe: (fn: (value: boolean) => void) => () => void }
  ): Promise<() => void> {
    return () => { };
  }

  /**
   * Upload file using browser File API
   */
  async uploadFile(
    file: File | string,
    options: FileUploadOptions,
    authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>
  ): Promise<FileUploadResult> {
    if (typeof file === 'string') {
      throw new Error('File path not supported in browser environment');
    }

    const { uploadToRagService } = await import('@repo/sharedlib/src/lib/fileUpload');

    return await uploadToRagService(file, options, authToken);
  }

  /**
   * Setup drag-and-drop using browser drag-and-drop API
   */
  async setupFileDragDrop(
    elementOrSelector: HTMLElement | string,
    onFiles: (files: import('@repo/common/types/SharedLib').FileUploadInput[]) => void,
    accept: string[] = ['pdf', 'docx', 'xlsx', 'doc']
  ): Promise<() => void> {
    const element =
      typeof elementOrSelector === 'string'
        ? (document.querySelector(elementOrSelector) as HTMLElement)
        : elementOrSelector;

    if (!element) {
      throw new Error('Element not found for drag-and-drop setup');
    }

    let dragCounter = 0;

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter++;
      element.classList.add('drag-over');
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter--;
      if (dragCounter === 0) {
        element.classList.remove('drag-over');
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter = 0;
      element.classList.remove('drag-over');

      const files = Array.from(e.dataTransfer?.files || []);
      const validFiles: import('@repo/common/types/SharedLib').FileUploadInput[] = [];

      for (const file of files) {
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (ext && accept.includes(ext)) {
          validFiles.push({
            file,
            filename: file.name,
            size: file.size,
            type: file.type
          });
        }
      }

      if (validFiles.length > 0) {
        onFiles(validFiles);
      }
    };

    element.addEventListener('dragenter', handleDragEnter);
    element.addEventListener('dragleave', handleDragLeave);
    element.addEventListener('dragover', handleDragOver);
    element.addEventListener('drop', handleDrop);

    // Cleanup function
    return () => {
      element.removeEventListener('dragenter', handleDragEnter);
      element.removeEventListener('dragleave', handleDragLeave);
      element.removeEventListener('dragover', handleDragOver);
      element.removeEventListener('drop', handleDrop);
      element.classList.remove('drag-over');
    };
  }
}
